import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, DOCUMENT } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as Sentry from '@sentry/angular';
import {
  Translation,
  TranslocoService,
  getBrowserLang,
  translateSignal,
} from '@jsverse/transloco';
import {
  Actions,
  createEffect,
  ofType,
  ROOT_EFFECTS_INIT,
} from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { SERVER_ERROR_CODES } from 'src/app/shared/constants/http';
import { selectCurrentMenu } from 'src/app/shared/ngrx/menus';
import { State } from 'src/app/reducers';
import { LocationGroup } from 'src/app/shared/Models/location';
import { ConfigService } from 'src/app/shared/Services/config/config.service';
import { setUser } from 'src/app/shared/user/ngrx/user.actions';
import { EMPTY } from 'rxjs';
import {
  catchError,
  concatMap,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { PreviewComponent } from '../Components/pdf-preview/pdf-preview.component';
import { GeneralFormErrors } from '../Models/authentication';
import { Additive, Allergen, Label } from '../Models/declarations';
import { Diet } from '../Models/diet';
import {
  CondensedDish,
  Dish,
  SimpleDishAdditives,
  SimpleDishAllergens,
} from '../Models/dish';
import { Results } from '../Models/generics';
import { Ingredient } from '../Models/ingredients';
import { SimpleLocation } from '../Models/location';
import { GenerateArchiveOptions } from '../Models/menu';
import { MTFont } from '../Models/mtfont';
import { OnboardingTemplate } from '../Models/onboarding_template';
import { NO_PARTNER, PARTNERS } from '../Models/partners';
import { Recipe } from '../Models/recipe';
import { Ruleset } from '../Models/ruleset';
import { CondensedSeparator } from '../Models/separator';
import { Layout } from '../Models/template';
import { Type } from '../Models/type';
import { Task, UserModule } from '../Models/user';
import { GenericsService } from '../Services/generics/generics.service';
import { UtilsService } from '../Services/utils/utils.service';
import { extractGeneralFormErrors, flattenObject } from '../utils.functions';
import * as SharedActions from './shared.actions';
import { Language } from '../Models/models';
import { DateAdapter } from '@angular/material/core';
import { de, enUS, es, fr, it } from 'date-fns/locale';
import { MatIconRegistry } from '@angular/material/icon';

import { Meta } from '@angular/platform-browser';
import { SimpleCollection, SimpleIntegration } from '../Models/integration';
import { sharedFeature } from './shared.state';
import { fetchUserOnboardingTemplates } from 'src/app/dashboard/ngrx/dashboard.actions';
import { isNil, omitBy } from 'lodash-es';

const NON_AUTH_FORM_ERRORS = [`message`, `token`, `error`];

@Injectable()
export class SharedEffects {
  private _document = inject(DOCUMENT);
  private actions$ = inject(Actions);
  private configService = inject(ConfigService);
  private dateAdapter = inject<DateAdapter<Date>>(DateAdapter);
  private dialog = inject(MatDialog);
  private genericsService = inject(GenericsService);
  private http = inject(HttpClient);
  private matIconRegistry = inject(MatIconRegistry);
  private meta = inject(Meta);
  private snackBar = inject(MatSnackBar);
  private store = inject<Store<State>>(Store);
  private transloco = inject(TranslocoService);
  private utils = inject(UtilsService);

  afterEffectsInit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROOT_EFFECTS_INIT),
      mergeMap(() => {
        this.matIconRegistry.setDefaultFontSetClass(
          'material-symbols-outlined',
        );
        const lang = Language(getBrowserLang());
        const actions: Action[] = [SharedActions.setInterfaceLang({ lang })];
        const userModules = this.utils.userModules;
        if (userModules)
          actions.push(SharedActions.setUserModules({ userModules }));
        return actions;
      }),
      catchError(() => EMPTY),
    ),
  );

  setInterfaceLang$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.setInterfaceLang),
      tap(({ lang }) => {
        this.transloco.setActiveLang(lang);
        this.dateAdapter.setLocale(this.localeMap[lang]);
      }),
      mergeMap(({ lang }) => [SharedActions.setGlobalMetaTags({ lang })]),
      catchError(() => EMPTY),
    ),
  );

  setGlobalMetaTags$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SharedActions.setGlobalMetaTags),
        tap(({ lang }) => {
          // Set the language attribute on the <html> tag
          this._document.querySelector('html').setAttribute('lang', lang);
        }),
        switchMap(({ lang }) =>
          this.transloco.selectTranslateObject<Record<string, string>>(
            'app.meta',
            undefined,
            lang,
          ),
        ),
        mergeMap((metaTags) => {
          if (!metaTags || typeof metaTags === 'string') return EMPTY;
          // Set all meta tags defined in the translations object
          Object.entries(metaTags).forEach(([key, value]) => {
            this.meta.updateTag({
              name: key,
              content: value,
            });
          });
          return EMPTY;
        }),
      ),
    { dispatch: false },
  );

  showSnackbarMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SharedActions.showSnackbarMessage),
        switchMap(({ errorCode, message, snackClass, button }) => {
          if (message && message !== `<`) {
            this.snackBar.open(message, button, {
              panelClass: [snackClass, 'cy-snackbar'],
              duration: 3000,
            });
          }
          if (errorCode === 0)
            this.snackBar.open(this.offlineErrorMessage(), null, {
              duration: 3000,
              panelClass: ['error-snack', 'cy-snackbar'],
            });
          if (SERVER_ERROR_CODES.includes(errorCode))
            this.snackBar.open(this.serverErrorMessage(), null, {
              duration: 3000,
              panelClass: ['error-snack', 'cy-snackbar'],
            });
          return [EMPTY];
        }),
        catchError(() => EMPTY),
      ),
    { dispatch: false },
  );

  handleHttpError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.handleHttpError),
      switchMap(({ error, formId, forceSnackbar }) => {
        // Check if this is actually an HTTP error
        if (!(error instanceof HttpErrorResponse)) {
          Sentry.captureException(error);
          return EMPTY;
        }

        if (!error?.error) return EMPTY;
        // handle form/field error
        let errors: GeneralFormErrors = extractGeneralFormErrors(error.error);
        if (SERVER_ERROR_CODES.includes(error.status)) {
          return [
            SharedActions.showSnackbarMessage({
              message: this.serverErrorMessage(),
            }),
          ];
        }
        if (error.status === 400 && errors && formId && !forceSnackbar) {
          if (NON_AUTH_FORM_ERRORS.some((key) => key in errors)) {
            errors = Object.entries(errors).reduce((acc, [, value]) => {
              acc['non_field_errors'] = value;
              return acc;
            }, {});
          }
          const actionsToDispatch = [];
          // TODO: setFieldErrors and selectGeneralFormErrors are only partially implemented (requires shared server-field directives)
          // NOTE: renamed setGeneralFormErrors -> setFieldErrors; rename setFormErrors -> setFormErrors
          if (Object.keys(errors).length > 1 || !errors?.non_field_errors) {
            actionsToDispatch.push(
              SharedActions.setFieldErrors({ payload: errors }),
            );
          }
          if (
            errors.non_field_errors &&
            typeof errors.non_field_errors === 'string'
          ) {
            actionsToDispatch.push(
              SharedActions.setFormErrors({
                error: { form_id: formId, message: errors.non_field_errors },
              }),
            );
          }
          return actionsToDispatch as [];
        }
        const flatObj = flattenObject(error.error);
        const firstErrorMessage =
          typeof flatObj === 'string' ? flatObj : Object.values(flatObj)[0];
        if (firstErrorMessage)
          return [
            SharedActions.showSnackbarMessage({ message: firstErrorMessage }),
          ];
        return EMPTY;
      }),
      catchError(() => EMPTY),
    ),
  );

  showPreviewDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SharedActions.showPreviewDialog),
        withLatestFrom(this.store.select(sharedFeature.selectCurrentLocation)),
        switchMap(
          ([
            {
              url,
              baseLanguage,
              numberLanguages,
              langs,
              multiRequired,
              hideLangs,
              params,
              htmlPreview,
            },
            location,
          ]) => {
            this.dialog.open(PreviewComponent, {
              autoFocus: false,
              width: '890px',
              maxWidth: '90vw',
              height: '95%',
              data: {
                url,
                baseLanguage,
                numberLanguages: numberLanguages || 1,
                langs: langs || [],
                multiRequired,
                hideLangs,
                params: UtilsService.addLocationParam(location, params),
                htmlPreview,
              },
            });
            return EMPTY;
          },
        ),
        catchError((error) => [SharedActions.handleHttpError({ error })]),
      ),
    { dispatch: false },
  );

  fetchPreviewHtml$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.fetchPreviewHtml),
      withLatestFrom(this.store.select(sharedFeature.selectCurrentLocation)),
      switchMap(([{ url, params }, location]) =>
        this.genericsService
          .get(
            url,
            UtilsService.addLocationParam(location, {
              ...params,
              response: 'html',
            }),
            undefined,
            'text',
          )
          .pipe(
            mergeMap((html: string) => [
              SharedActions.setPreviewHtml({ html }),
            ]),
            // handleHttpError takes {error}: passing the response itself made
            // `error instanceof HttpErrorResponse` false, so the failure was
            // reported to Sentry as a bare object and the user was told nothing.
            catchError((error) => [
              SharedActions.handleHttpError({ error }),
              SharedActions.setPreviewFailed(),
            ]),
          ),
      ),
    ),
  );

  fetchPreviewPdfHtml$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.fetchPreviewPdfHtml),
      withLatestFrom(this.store.select(sharedFeature.selectCurrentLocation)),
      switchMap(([{ url, params }, location]) =>
        this.genericsService
          .get(
            url,
            UtilsService.addLocationParam(location, {
              ...params,
              response: 'html',
            }),
            undefined,
            'text',
          )
          .pipe(
            mergeMap((html: string) => [
              SharedActions.setPreviewPdfHtml({ html }),
            ]),
            catchError((error) => [
              SharedActions.handleHttpError({ error }),
              SharedActions.setPreviewFailed(),
            ]),
          ),
      ),
    ),
  );

  onUserCategoryChanged$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setUser),
        filter(({ user }) => !!user?.category),
        map(({ user }) => [
          `assets/i18n/${user.settings.language}_${user.category}.json`,
          user.settings.language,
        ]),
        distinctUntilChanged((prev, curr) => prev[0] === curr[0]),
        switchMap(([path, lang]) =>
          this.http.get(path).pipe(
            map((translations) => [translations, lang]),
            catchError((error) => {
              return [SharedActions.handleHttpError({ error })];
            }),
          ),
        ),
        map((dataOrError) => {
          if (Array.isArray(dataOrError)) {
            const translations = dataOrError[0];
            const lang = dataOrError[1] as string;
            this.transloco.setTranslation(translations as Translation, lang, {
              merge: true,
            });
          }
        }),
      ),
    { dispatch: false },
  );

  getAllLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.getAllLocations),
      switchMap(({ params }) =>
        this.genericsService
          .get<SimpleLocation[]>(this.configService.locations, params)
          .pipe(
            mergeMap((locations) => [
              SharedActions.setAllLocations({ payload: locations }),
            ]),
            catchError((error) => [SharedActions.handleHttpError({ error })]),
          ),
      ),
    ),
  );

  getAllLocationGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.getAllLocationGroups),
      switchMap(({ params }) =>
        this.genericsService
          .get<LocationGroup[]>(this.configService.locationGroups, params)
          .pipe(
            mergeMap((locations) => [
              SharedActions.setAllLocationGroups({ payload: locations }),
            ]),
            catchError((error) => [SharedActions.handleHttpError({ error })]),
          ),
      ),
    ),
  );

  fetchTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.fetchTemplates),
      tap(() => {
        this.store.dispatch(SharedActions.setTemplatesState({ loading: true }));
      }),
      switchMap(() =>
        this.genericsService
          .get<OnboardingTemplate[]>(this.configService.onboardingTemplates)
          .pipe(
            mergeMap((data) => [
              SharedActions.setTemplates({ data }),
              SharedActions.setTemplatesState({ loading: false }),
            ]),
            catchError((error) => [
              SharedActions.setTemplatesState({ loading: false }),
              SharedActions.handleHttpError({ error }),
            ]),
          ),
      ),
    ),
  );

  fetchTemplatesMenu$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.fetchTemplatesMenu),
      switchMap(({ current_menu }) =>
        this.genericsService
          .get<OnboardingTemplate[]>(this.configService.onboardingTemplates, {
            current_menu,
          })
          .pipe(
            mergeMap((data) => [SharedActions.setTemplatesLocation({ data })]),
            catchError((error) => [SharedActions.handleHttpError({ error })]),
          ),
      ),
    ),
  );

  fetchOrderTakingLayouts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.fetchOrderTakingLayouts),
      withLatestFrom(this.store.select(selectCurrentMenu)),
      switchMap(([{ params }, currentMenu]) =>
        this.genericsService
          .get<Layout[]>(this.configService.layouts, {
            ...params,
            ...(currentMenu ? { current_menu: currentMenu?.id } : {}),
            category: 'order',
          })
          .pipe(
            mergeMap((layouts) => [
              SharedActions.setOrderTakingLayouts({ payload: layouts }),
            ]),
            catchError((error) => [SharedActions.handleHttpError({ error })]),
          ),
      ),
    ),
  );

  fetchUsedTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.fetchUsedTemplates),
      withLatestFrom(this.store.select(sharedFeature.selectCurrentLocation)),
      switchMap(([{ params }, location]) =>
        this.genericsService
          .get<OnboardingTemplate[]>(
            this.configService.onboardingTemplates,
            UtilsService.addLocationParam(location, { ...params, used: true }),
          )
          .pipe(
            mergeMap((templates) => [
              SharedActions.setUsedTemplates({ payload: templates }),
            ]),
            catchError((error) => [SharedActions.handleHttpError({ error })]),
          ),
      ),
    ),
  );

  fetchPartnerByCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.fetchPartnerByCode),
      switchMap(({ code }) => [
        SharedActions.setPartner({
          partner: PARTNERS.find((p) => p.code === code) || NO_PARTNER,
        }),
      ]),
    ),
  );

  uploadImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.uploadImage),
      withLatestFrom(this.store.select(sharedFeature.selectCurrentLocation)),
      switchMap(([{ actions, callback, image, url, params }, location]) =>
        this.genericsService
          .patch<FormData, any>(
            url,
            image,
            UtilsService.addLocationParam(location, params),
          )
          .pipe(
            mergeMap((result) => {
              if (callback) callback(result);
              if (actions?.length)
                return actions.map(([action, key]) =>
                  action({ [key]: result }),
                );
              return [];
            }),
            catchError((error) => [SharedActions.handleHttpError(error)]),
          ),
      ),
    ),
  );

  fetchRules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.fetchRules),
      withLatestFrom(this.store.select(sharedFeature.selectCurrentLocation)),
      switchMap(([{ params }, location]) =>
        this.genericsService
          .get<Results<Ruleset>>(
            this.configService.rules,
            UtilsService.addLocationParam(location, params),
          )
          .pipe(
            mergeMap((rules) => [SharedActions.setRules({ payload: rules })]),
            catchError((error) => [SharedActions.handleHttpError({ error })]),
          ),
      ),
    ),
  );

  sendSupportForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.sendSupportForm),
      switchMap(({ callback, files, ...data }) => {
        const formData = new FormData();
        Object.entries(data).forEach(([k, v]) => formData.append(k, v));
        if (files?.length) files.forEach((f) => formData.append('files', f));
        return this.genericsService
          .post<FormData, { message: string }>(
            this.configService.support,
            formData,
          )
          .pipe(
            mergeMap(() => {
              callback?.();
              return EMPTY;
            }),
            catchError((error) => [SharedActions.handleHttpError({ error })]),
          );
      }),
    ),
  );

  fetchFonts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.fetchFonts),
      switchMap(() =>
        this.genericsService.get<MTFont[]>(this.configService.fonts).pipe(
          mergeMap((fonts: MTFont[]) => [
            SharedActions.setFontsAsFilter({ fonts: fonts }),
          ]),
          catchError((error) => [SharedActions.handleHttpError({ error })]),
        ),
      ),
    ),
  );

  fetchDishesAutocomplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.fetchDishesAutocomplete),
      withLatestFrom(this.store.select(sharedFeature.selectCurrentLocation)),
      switchMap(([{ params }, location]) =>
        this.genericsService
          .get<Results<CondensedDish>>(
            this.configService.dishes,
            UtilsService.addLocationParam(location, {
              condensed: true,
              ...params,
            }),
          )
          .pipe(
            mergeMap((dishes) => [
              SharedActions.setDishesAutocomplete({ payload: dishes }),
            ]),
            catchError((error) => [SharedActions.handleHttpError({ error })]),
          ),
      ),
    ),
  );

  fetchIngredientsAutocomplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.fetchIngredientsAutocomplete),
      withLatestFrom(this.store.select(sharedFeature.selectCurrentLocation)),
      switchMap(([{ params }, location]) => {
        return this.genericsService
          .get<Results<Ingredient>>(
            this.configService.ingredients,
            UtilsService.addLocationParam(location, params),
          )
          .pipe(
            mergeMap((res) => [
              SharedActions.setIngredientsAutocomplete({ payload: res }),
            ]),
            catchError((error) => [SharedActions.handleHttpError({ error })]),
          );
      }),
    ),
  );

  createIngredientAutocomplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.createIngredientAutocomplete),
      withLatestFrom(this.store.select(sharedFeature.selectCurrentLocation)),
      switchMap(([{ ingredient, params, onFulfilled }, location]) =>
        this.genericsService
          .post<Partial<Ingredient>, Ingredient>(
            this.configService.ingredients,
            ingredient,
            UtilsService.addLocationParam(location, params),
          )
          .pipe(
            mergeMap((res) => {
              if (onFulfilled) onFulfilled(res);
              return [
                SharedActions.setIngredientsAutocomplete({
                  payload: {
                    results: [res],
                    count: 1,
                    next: null,
                    previous: null,
                  },
                }),
              ];
            }),
            catchError((error) => [SharedActions.handleHttpError({ error })]),
          ),
      ),
    ),
  );

  updateIngredientAutocomplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.updateIngredientAutocomplete),
      withLatestFrom(this.store.select(sharedFeature.selectCurrentLocation)),
      switchMap(([{ url, ingredient, params, onFulfilled }, location]) =>
        this.genericsService
          .patch<Partial<Ingredient>, Ingredient>(
            url,
            ingredient,
            UtilsService.addLocationParam(location, params),
          )
          .pipe(
            mergeMap((res) => {
              if (onFulfilled) onFulfilled(res);
              return [];
            }),
            catchError((error) => [SharedActions.handleHttpError({ error })]),
          ),
      ),
    ),
  );

  createDish$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.createDishAutocomplete),
      withLatestFrom(this.store.select(sharedFeature.selectCurrentLocation)),
      switchMap(([{ dish, clear, callback, errorCb }, location]) =>
        this.genericsService
          .post<Partial<Dish>, Dish>(
            this.configService.dishes,
            dish,
            UtilsService.addLocationParam(location),
          )
          .pipe(
            mergeMap((res) => {
              if (callback) callback(res);
              return clear
                ? []
                : [
                    SharedActions.setDishesAutocomplete({
                      payload: {
                        count: 1,
                        next: null,
                        previous: null,
                        results: [res as CondensedDish],
                      },
                    }),
                  ];
            }),
            catchError((error) => {
              if (errorCb) errorCb();
              return [SharedActions.handleHttpError({ error })];
            }),
          ),
      ),
    ),
  );

  fetchUserModules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.fetchUserModules),
      switchMap(() =>
        this.genericsService
          .get<UserModule[]>(this.configService.userModules)
          .pipe(
            tap((userModules) => {
              this.utils.userModules = userModules;
            }),
            mergeMap((userModules) => {
              // load data based on modules
              const activeModules = userModules.map(
                (m) => m.module_detail.code,
              );
              if (activeModules.includes('loca')) {
                this.store.dispatch(
                  SharedActions.getAllLocations({
                    params: {
                      condensed: true,
                      ordering: 'name',
                      pagination: 'off',
                    },
                  }),
                );
                this.store.dispatch(
                  SharedActions.getAllLocationGroups({
                    params: {
                      condensed: true,
                      ordering: 'name',
                      pagination: 'off',
                    },
                  }),
                );
              }
              if (activeModules.includes('order')) {
                this.store.dispatch(
                  SharedActions.fetchOrderTakingLayouts({
                    params: { condensed: true, pagination: 'off' },
                  }),
                );
              }
              return [
                SharedActions.setUserModules({ userModules }),
                SharedActions.setUserModulesLoaded(),

                // load generic data
                SharedActions.fetchDeclarations({}),
                SharedActions.fetchTasks(),
                SharedActions.fetchTemplates(),
                SharedActions.fetchFonts(),

                // load onboarding user templates (for write new menu)
                fetchUserOnboardingTemplates(),
              ];
            }),
            catchError((error) => [SharedActions.handleHttpError({ error })]),
          ),
      ),
    ),
  );

  fetchAllergens$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.fetchAllergens),
      withLatestFrom(this.store.select(sharedFeature.selectCurrentLocation)),
      switchMap(([{ params }, location]) =>
        this.genericsService
          .get<Allergen[]>(
            this.configService.allergens,
            !params?.current_location
              ? UtilsService.addLocationParam(location, params ?? {})
              : omitBy({ ...params }, isNil),
          )
          .pipe(
            map((result) => SharedActions.setAllergens({ result })),
            catchError((error) => [SharedActions.handleHttpError({ error })]),
          ),
      ),
    ),
  );

  fetchAdditives$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.fetchAdditives),
      withLatestFrom(this.store.select(sharedFeature.selectCurrentLocation)),
      switchMap(([{ params }, location]) =>
        this.genericsService
          .get<Additive[]>(
            this.configService.additives,
            !params?.current_location
              ? UtilsService.addLocationParam(location, params ?? {})
              : omitBy({ ...params }, isNil),
          )
          .pipe(
            map((result) => SharedActions.setAdditives({ result })),
            catchError((error) => [SharedActions.handleHttpError({ error })]),
          ),
      ),
    ),
  );

  fetchLabel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.fetchLabels),
      withLatestFrom(this.store.select(sharedFeature.selectCurrentLocation)),
      switchMap(([{ params }, location]) =>
        this.genericsService
          .get<Label[]>(
            this.configService.labels,
            !params?.current_location
              ? UtilsService.addLocationParam(location, params ?? {})
              : omitBy({ ...params }, isNil),
          )
          .pipe(
            map((result) => SharedActions.setLabels({ result })),
            catchError((error) => [SharedActions.handleHttpError({ error })]),
          ),
      ),
    ),
  );

  fetchDeclarations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.fetchDeclarations),
      withLatestFrom(
        this.store.select(sharedFeature.selectUserModulesActiveCodes),
      ),
      switchMap(([params, modules]) => {
        const actions: Action[] = [];
        if (modules.includes('all'))
          actions.push(SharedActions.fetchAllergens(params));
        if (modules.includes('add'))
          actions.push(SharedActions.fetchAdditives(params));
        if (modules.includes('label'))
          actions.push(SharedActions.fetchLabels(params));
        return actions;
      }),
    ),
  );

  fetchArchives$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.fetchTasks),
      withLatestFrom(this.store.select(sharedFeature.selectTasks)),
      switchMap(([, previousTasks]) =>
        this.genericsService.get<Task[]>(this.configService.menuArchives).pipe(
          mergeMap((tasks) => {
            // check if any of the previousTasks is not ready but is now in the archives and ready
            tasks
              .filter((t) => [1, 3, 5].includes(t.task_type))
              .forEach((task) => {
                const archive = previousTasks.find((t) => t.id === task.id);
                if (archive && archive.ready && !task.ready) {
                  this.utils.refreshPdfView.next();
                }
              });
            // repeat if any of the tasks is not ready
            if (tasks?.find((archive) => !archive.ready && !archive.failed)) {
              setTimeout(
                () => this.store.dispatch(SharedActions.fetchTasks()),
                3000,
              );
            }
            return [SharedActions.setTasks({ tasks })];
          }),
          catchError((error) => [SharedActions.handleHttpError(error)]),
        ),
      ),
    ),
  );

  changeArchive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.changeArchive),
      switchMap(({ archive }) =>
        this.genericsService
          .patch<Partial<Task>, Task>(archive.url, archive)
          .pipe(
            mergeMap(() => [SharedActions.fetchTasks()]),
            catchError((error) => [SharedActions.handleHttpError({ error })]),
          ),
      ),
    ),
  );

  createArchive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.createArchive),
      withLatestFrom(
        this.store.select(sharedFeature.selectCurrentLocation),
        this.store.select(sharedFeature.selectTasks),
      ),
      switchMap(([{ menu, task_type, options }, location, tasks]) => {
        const isDownloading = tasks.find(
          (el) =>
            el.object_id === menu.id &&
            el.task_type === task_type &&
            el.ready === false &&
            el.content_type === 'menu',
        );
        if (isDownloading) {
          return [
            SharedActions.showSnackbarMessage({
              message: this.taskInProgressMessage(),
            }),
          ];
        }
        return this.genericsService
          .post<GenerateArchiveOptions, { message: string }>(
            `${menu.url}archive/`,
            {
              task_type,
              ...options,
            },
            UtilsService.addLocationParam(location),
          )
          .pipe(
            mergeMap(() => [SharedActions.fetchTasks()]),
            catchError((error) => [SharedActions.handleHttpError({ error })]),
          );
      }),
    ),
  );

  fetchSeparatorAutocomplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.fetchSeparatorAutocomplete),
      switchMap(({ params }) =>
        this.genericsService
          .get<Results<CondensedSeparator>>(this.configService.separators, {
            category: 'sec',
            condensed: true,
            ...params,
          })
          .pipe(
            mergeMap((separators) => [
              SharedActions.setAutocompleteSeparator({
                payload: separators.results,
              }),
            ]),
            catchError((error) => [SharedActions.handleHttpError({ error })]),
          ),
      ),
    ),
  );

  fetchDiets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.fetchDiets),
      withLatestFrom(this.store.select(sharedFeature.selectCurrentLocation)),
      switchMap(([{ params }, location]) =>
        this.genericsService
          .get<Diet[]>(
            this.configService.diets,
            UtilsService.addLocationParam(location, {
              ...params,
              pagination: 'off',
            }),
          )
          .pipe(
            mergeMap((diets) => [SharedActions.setDiets({ diets })]),
            catchError((error) => [SharedActions.handleHttpError({ error })]),
          ),
      ),
    ),
  );

  fetchTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.fetchTypes),
      withLatestFrom(this.store.select(sharedFeature.selectCurrentLocation)),
      switchMap(([{ params }, location]) =>
        this.genericsService
          .get<Type[]>(
            this.configService.types,
            UtilsService.addLocationParam(location, {
              ...params,
              pagination: 'off',
            }),
          )
          .pipe(
            mergeMap((data) => [SharedActions.setTypes({ data })]),
            catchError((error) => [SharedActions.handleHttpError({ error })]),
          ),
      ),
    ),
  );

  fetchRecipesAutocomplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.fetchRecipesAutocomplete),
      withLatestFrom(this.store.select(sharedFeature.selectCurrentLocation)),
      switchMap(([{ params }, location]) =>
        this.genericsService
          .get<Results<Recipe>>(
            this.configService.recipes,
            UtilsService.addLocationParam(location, {
              ...params,
              condensed: true,
            }),
          )
          .pipe(
            mergeMap((recipes) => [
              SharedActions.setRecipes({ recipes: recipes.results }),
            ]),
            catchError((error) => [SharedActions.handleHttpError({ error })]),
          ),
      ),
    ),
  );

  fetchSimilarDishes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.fetchSimilarDishes),
      withLatestFrom(this.store.select(selectCurrentMenu)),
      concatMap(([{ name, lang, containsOption, dish }, currentMenu]) => {
        const baseLang = currentMenu?.base_language || lang;
        const params = {
          category: dish.category,
          condensed: true,
          exclude: dish.id,
          [`${baseLang}_similar`]: name,
          [`has_${containsOption}`]: true,
          ...(currentMenu?.id ? { current_menu: currentMenu?.id } : {}),
          ...(lang && lang !== baseLang ? { [`${lang}_isempty`]: false } : {}),
        };
        return this.genericsService
          .get<Results<SimpleDishAllergens | SimpleDishAdditives>>(
            this.configService.dishes,
            params,
          )
          .pipe(
            mergeMap((result) => {
              if (containsOption === 'additives') {
                return [
                  SharedActions.setSimilarDishesAdditives({
                    result: result as Results<SimpleDishAdditives>,
                  }),
                ];
              }
              return [
                SharedActions.setSimilarDishesAllergens({
                  result: result as Results<SimpleDishAllergens>,
                }),
              ];
            }),
            catchError((error) => [SharedActions.handleHttpError({ error })]),
          );
      }),
    ),
  );

  fetchMoreDishes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.fetchMoreDishes),
      withLatestFrom(
        this.store.select(
          sharedFeature.selectSimilarDishesAllergensNextPageUrl,
        ),
        this.store.select(
          sharedFeature.selectSimilarDishesAdditivesNextPageUrl,
        ),
      ),
      switchMap(([{ typeOfDish }, nextPageUrl, nextPageUrlAdditives]) => {
        const nextPage =
          typeOfDish === 'additives' ? nextPageUrlAdditives : nextPageUrl;
        if (!nextPage) return undefined;
        return this.genericsService
          .get<Results<SimpleDishAllergens | SimpleDishAdditives>>(nextPage)
          .pipe(
            mergeMap(({ next: nextUrl, results }) => {
              if (typeOfDish === 'additives') {
                return [
                  SharedActions.addMoreDishesAdditives({
                    dishes: results as SimpleDishAdditives[],
                    nextUrl,
                  }),
                ];
              } else {
                return [
                  SharedActions.addMoreDishes({
                    dishes: results as SimpleDishAllergens[],
                    nextUrl,
                  }),
                ];
              }
            }),
            catchError((error) => [SharedActions.handleHttpError({ error })]),
          );
      }),
    ),
  );

  fetchCollections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.fetchCollections),
      switchMap(() =>
        this.genericsService
          .get<SimpleCollection[]>(this.configService.collections, {
            pagination: 'off',
            condensed: true,
          })
          .pipe(
            mergeMap((collections) => [
              SharedActions.setCollections({
                collections,
              }),
            ]),
            catchError((error) => [SharedActions.handleHttpError({ error })]),
          ),
      ),
    ),
  );

  fetchLinks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.fetchLinks),
      switchMap(() =>
        this.genericsService
          .get<SimpleIntegration[]>(this.configService.integrations, {
            pagination: 'off',
            condensed: true,
          })
          .pipe(
            mergeMap((links) => [
              SharedActions.setLinks({
                links,
              }),
            ]),
            catchError((error) => [SharedActions.handleHttpError({ error })]),
          ),
      ),
    ),
  );

  setCurrentLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.setCurrentLocation),
      switchMap(() => [SharedActions.fetchDeclarations({})]),
    ),
  );

  private readonly serverErrorMessage = translateSignal(
    'shared.errors.server-error',
  );
  private readonly offlineErrorMessage = translateSignal(
    'shared.errors.offline',
  );
  private readonly taskInProgressMessage = translateSignal(
    'shared.errors.task-in-progress',
  );

  localeMap = {
    en: enUS,
    de: de,
    fr: fr,
    es: es,
    it: it,
  };
}
